import { Component, ViewChild } from '@angular/core';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/icon/icon.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { COLORS, DIALOG_WIDTH } from 'src/app/shared/constants';
import { FilterType, TableDataType, TextFormat, } from 'src/app/shared/enums';
import { InlineActionComponent } from 'src/app/components/data-table/inline-action/inline-action.component';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast.service';
import { LoadingService } from 'src/app/services/loading.service';
import { IColumnConfig, ITableConfig, ITopOptions } from 'src/app/shared/interfaces/ITable';
import { DataTableComponent } from 'src/app/components/data-table/data-table.component';
import { InformationService } from 'src/app/services/information.service';
import { TextFormatterComponent } from 'src/app/components/data-table/text-formatter/text-formatter.component';

@Component({
  selector: 'app-information',
  standalone: true,
  imports: [MaterialModule, IconModule, TranslateModule, ReactiveFormsModule, DataTableComponent],
  templateUrl: './information.component.html',
})
export class InformationComponent {
  @ViewChild(DataTableComponent) dataTable: DataTableComponent;
  tableConfig: ITableConfig;
  topOptions: ITopOptions = {};
  isAdmin = false;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private informationService: InformationService) {
      this.isAdmin = this.router.url.startsWith('/admin');
    }

  ngOnInit() {
    this.initTopAction();
    this.initColumn();
  }

  initTopAction() {
    this.topOptions = {
      showSearch: true,
      topActions: this.isAdmin ? [
        {
          name: 'add',
          color: COLORS.BLUE,
          action: () => {
            this.openInformationDetail()
          }
        }
      ] : []
    }
  }

  initColumn() {
    const AdminColumns: IColumnConfig[] = [
      {
        title: 'subject',
        key: 'subject',
        sortable: true,
        searchable: true,
        filterable: FilterType.TEXT,
      },
      {
        title: 'created date',
        key: 'createdDate',
        sortable: true,
        searchable: true,
        cellDataType: TableDataType.DYNAMIC_COMPONENT,
        filterable: FilterType.DATE,
        titleStyle: { width: '150px' },
        cellValue: (cell) => [TextFormatterComponent, { cell, format: TextFormat.DATE_TIME_ZONE }]
      },
      {
        title: 'created by',
        key: 'createdByName',
        sortable: true,
        searchable: true,
        filterable: FilterType.TEXT,
      }
    ];

    const userColumns: IColumnConfig[] = [
      {
        title: 'created date',
        key: 'createdDate',
        sortable: true,
        searchable: true,
        cellDataType: TableDataType.DYNAMIC_COMPONENT,
        titleStyle: { width: '150px' },
        cellValue: (cell) => [TextFormatterComponent, { cell, format: TextFormat.DATE_TIME_ZONE }]
      },
      {
        title: 'subject',
        key: 'subject',
        sortable: true,
        searchable: true,
        titleStyle: { width: '250px', maxWidth: '320px' },
        cellStyle: { width: '250px', maxWidth: '320px' }
      },
      {
        title: 'content',
        key: 'message',
        sortable: true,
        searchable: true,
        cellDataType: TableDataType.HTML,
        cellValue: ({ row }) => this.getEditorContent(row.message)
      },
    ];

    this.tableConfig = {
      columnConfig:
        [
          {
            key: 'inline-action',
            cellStyle: { width: '50px' },
            cellDataType: TableDataType.DYNAMIC_COMPONENT,
            cellValue: ({ row }) => this.isAdmin ? [
              InlineActionComponent,
              {
                row,
                dropdownIcon: 'dots',
                rightClickShowDropdown: true,
                style: { display: 'flex', justifyContent: 'center' },
                inlineActions: [
                  {
                    icon: 'edit',
                    name: 'edit',
                    action: () => this.onClickRow({ row })
                  },
                  {
                    icon: 'trash',
                    name: 'delete',
                    action: () => this.openConfirmDeleteDialog(row)
                  }
                ]
              }
            ] : []
          },
          ...(this.isAdmin ? AdminColumns : userColumns)
        ]
    }
  }

  fetchData = (params: any) => this.informationService.getInformations(params)

  onClickRow = ({ row }: any) => this.openInformationDetail(row.id)

  openInformationDetail(id?: number) {
    if (id) {
      this.router.navigate([`${this.isAdmin ? '/admin' : ''}/information/detail`, id]);
      return;
    }
    this.router.navigate([`${this.isAdmin ? '/admin' : ''}/information/detail`]);
  }

  openConfirmDeleteDialog(row: any) {
    this.toastService.confirmDelete({
      title: 'delete information',
      content: 'do you want to delete this',
      width: DIALOG_WIDTH.SMALLEST,
      onConfirm: () => {
        this.loadingService.show();
        this.informationService.deleteInformation(row.id).subscribe({
          next: () => {
            this.toastService.success(this.translate.instant('delete successfully'));
            this.dataTable.retrieveData();
            this.loadingService.hide();
          },
          error: ({ error }) => {
            this.loadingService.hide();
            this.toastService.error(this.translate.instant(error.Message));
          }
        });
      }
    });
  }

  getEditorContent(html: string): string {
    if (!html) return '';

    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('script, style').forEach(el => el.remove());
    div.querySelectorAll('img').forEach(el => {
      el.replaceWith(document.createTextNode(' [Image] '));
    });

    div.querySelectorAll('video, iframe').forEach(el => {
      el.replaceWith(document.createTextNode(' [Video] '));
    });
    const preview = document.createElement('div');
    preview.textContent = (div.textContent || '').replace(/\s+/g, ' ').trim();

    return `<div style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">${preview.innerHTML}</div>`;
  }
}
